import { Result } from '@core/models/result'
import { IdbDataStore } from '@core/storage/idb-data-store.ts'
import { CookieDataStore } from './stores/cookie-data-store'
import { MemDataStore } from './stores/mem-data-store'
import { Logger, TaggedLogger } from '@core/logging/logger'
import {
    StorageType,
    type AbstractDataStore,
    type DataStoreName,
    type TimestampedDataValue,
    SynchronousDataStore,
} from '@core/storage/abstract-data-store.ts'
import { SessionDataStore } from './stores/session-data-store.ts'
import { PNException } from '@core/exceptions/pn-exception.ts'
import { PNExceptionCode } from '@core/exceptions/pn-exception-code.ts'
import { StaticEventDispatcher } from '@core/static-event-dispatcher.ts'

type StorageOperationOptions = {
    preferredStores?: [StorageType, ...StorageType[]]
}

type StorageTypeList =
    | StorageType[]
    | readonly StorageType[]
    | [StorageType, ...StorageType[]]
    | readonly [StorageType, ...StorageType[]]

/** @internal */
export const StorageException = {
    notAvailable: (types: StorageTypeList) => new Error(`No available stores found for ${types.join(', ')}`),
    noData: (key: string) => new PNException(PNExceptionCode.STORAGE_KEY_EMPTY, null, key),
}

/** @internal */
export abstract class AbstractStorageManager {
    public readonly _storeName: DataStoreName | null

    protected readonly _logger: Logger

    private readonly _stores: Record<StorageType, AbstractDataStore | undefined>
    private readonly _preferredStores: ReadonlyArray<StorageType>

    /**
     * @param storeName     - The namespace to use within the SDK storage type.
     *                        When `null` is provided the idb and mem storage types
     *                        are disabled and the cookie storage type uses separate
     *                        cookies based on key name.
     * @param storageTypes  - The list of storage types used for this manager.
     *                        The order of the list determines the GET cycle
     *                        order. eg: [cookie, idb] would attempt to first
     *                        read from cookie storage before falling back to
     *                        idb storage if no matching key is found
     * @param logger        - Logger instance
     * @param sessionNamespace - Overrides the sessionStorage key for this manager, leaving its
     *                        IndexedDB object store unchanged. Worth setting when a manager
     *                        holds values large enough that sharing a blob with a frequently
     *                        written manager would make that manager's access expensive -
     *                        every session read and write re-serialises the whole blob.
     */
    protected constructor(
        storeName: DataStoreName | null,
        storageTypes: [StorageType, ...StorageType[]],
        logger: Logger = TaggedLogger._getInstance('StorageManager'),
        sessionNamespace?: string,
    ) {
        this._storeName = storeName
        this._preferredStores = storageTypes
        this._logger = logger

        this._stores = Object.fromEntries(
            this._preferredStores.map<[StorageType, AbstractDataStore | undefined]>((storageType) => {
                switch (storageType) {
                    case StorageType.IDB:
                        return [StorageType.IDB, new IdbDataStore(storeName)]
                    case StorageType.COOKIE:
                        return [StorageType.COOKIE, new CookieDataStore(storeName)]
                    case StorageType.LEGACY_COOKIE:
                        return [StorageType.LEGACY_COOKIE, new CookieDataStore(null)]
                    case StorageType.MEM:
                        return [StorageType.MEM, new MemDataStore(storeName)]
                    case StorageType.SESSION:
                        return [StorageType.SESSION, new SessionDataStore(storeName, undefined, sessionNamespace)]
                }
            }),
        ) as Record<StorageType, AbstractDataStore | undefined>
    }

    public _getAvailableStores(preferredStores?: [StorageType, ...StorageType[]]): Result<AbstractDataStore[]> {
        const preferred = preferredStores ?? this._preferredStores
        const stores = preferred.filter(
            (store: StorageType) => !!this._stores[store] && this._stores[store]!._isAvailable,
        )

        return stores.length === 0
            ? Result.Err(StorageException.notAvailable(preferred))
            : Result.Ok(stores.map((store) => this._stores[store]!))
    }

    public async _getData<T>(key: string, options?: StorageOperationOptions): Promise<Result<T>> {
        const value = await this._getTimestampedData<T>(key, options)
        if (value.isErr()) {
            return value
        }
        return Result.Ok(value.unwrap().value)
    }

    public _getDataSync<T>(key: string, options?: StorageOperationOptions): Result<T> {
        const value = this._getTimestampedDataSync<T>(key, options)
        if (value.isErr()) {
            return value
        }
        return Result.Ok(value.unwrap().value)
    }

    public async _getTimestampedData<T>(
        key: string,
        options?: StorageOperationOptions,
    ): Promise<Result<TimestampedDataValue<T>>> {
        const records = await this._mGetTimestampedData<T>(key, options)
        const values = Object.values(records)
            .filter((r) => r.isOk() && r.unwrap().value !== null)
            .map((r) => r.unwrap())

        if (!values.length) {
            return Result.Err(StorageException.noData(key))
        }

        if (values.length > 1) {
            values.sort((a, b) => b.ts - a.ts)
        }

        return Result.Ok(values[0])
    }

    public _getTimestampedDataSync<T>(key: string, options?: StorageOperationOptions): Result<TimestampedDataValue<T>> {
        const records = this._mGetTimestampedDataSync<T>(key, options)
        const values = Object.values(records)
            .filter((r) => r.isOk() && r.unwrap().value !== null)
            .map((r) => r.unwrap())

        if (!values.length) {
            return Result.Err(StorageException.noData(key))
        }

        // Newest wins, as on the async path. Resolving these two differently means a caller gets
        // a different answer depending on which one it happened to use.
        if (values.length > 1) {
            values.sort((a, b) => b.ts - a.ts)
        }

        return Result.Ok(values[0])
    }

    public async _mGetTimestampedData<T>(
        key: string,
        options?: StorageOperationOptions,
    ): Promise<Record<StorageType, Result<TimestampedDataValue<T>>>> {
        const results = {} as Record<StorageType, Result<TimestampedDataValue<T>>>

        const stores = options?.preferredStores ?? this._preferredStores

        type ResultRecord = [StorageType, Result<TimestampedDataValue<T>>]

        const txs = await Promise.all(
            stores.map<ResultRecord | Promise<ResultRecord>>((store) =>
                this._stores[store]?._isAvailable
                    ? this._stores[store]!._getData<T>(key).then((res) => [store, res])
                    : ([store, Result.Ok<TimestampedDataValue>({ value: null, ts: 0 })] as ResultRecord),
            ),
        )

        txs.forEach(([store, res]) => {
            results[store] = res
        })

        return results
    }

    public _mGetTimestampedDataSync<T>(
        key: string,
        options?: StorageOperationOptions,
    ): Record<StorageType, Result<TimestampedDataValue<T>>> {
        const results = {} as Record<StorageType, Result<TimestampedDataValue<T>>>

        const stores = options?.preferredStores ?? this._preferredStores

        type ResultRecord = [StorageType, Result<TimestampedDataValue<T>>]

        const txs = stores
            .filter((storageType) => this._stores[storageType] && '_getDataSync' in this._stores[storageType]!)
            .map((storageType) => this._stores[storageType]! as SynchronousDataStore)
            .map<ResultRecord>((store) =>
                store?._isAvailable
                    ? [store._storageType, store._getDataSync<T>(key)]
                    : [store._storageType, Result.Ok<TimestampedDataValue>({ value: null, ts: 0 })],
            )

        txs.forEach(([store, res]) => {
            results[store] = res
        })

        return results
    }

    public async _setData<T>(key: string, value: T, options?: StorageOperationOptions): Promise<Result<T>> {
        const stores = options?.preferredStores ?? this._preferredStores

        const txs = await Promise.all(
            stores.map((store) =>
                this._stores[store]?._isAvailable
                    ? this._stores[store]!._setData<T>(key, value).then((res) => {
                          if (res.isErr()) {
                              this._logger.warn(`Unable to set [${store}:error] ${key}`, res.getErr().message)
                          }
                          return res
                      })
                    : Promise.resolve(null).then((res) => {
                          this._logger.warn(`Unable to set [${store}:not_available] ${key}`)
                          return res
                      }),
            ),
        )

        return this._parseSetterResponse(txs, stores, value)
    }

    public _setDataSync<T>(key: string, value: T, options?: StorageOperationOptions): Result<T> {
        const stores = options?.preferredStores ?? this._preferredStores

        const txs = stores
            .filter((storageType) => this._stores[storageType] && '_getDataSync' in this._stores[storageType]!)
            .map((storageType) => this._stores[storageType]! as SynchronousDataStore)
            .map((store) => {
                if (store?._isAvailable) {
                    const res = store._setDataSync<T>(key, value)
                    if (res.isErr()) {
                        this._logger.warn(`Unable to set [${store}:error] ${key}`, res.getErr().message)
                    }
                    return res
                }

                this._logger.warn(`Unable to set [${store}:not_available] ${key}`)
                return null
            })

        return this._parseSetterResponse(txs, stores, value)
    }

    public async _removeData(key: string, options?: StorageOperationOptions): Promise<Result<null>> {
        const stores = options?.preferredStores ?? this._preferredStores

        const txs = await Promise.all(
            stores.map((store) => (this._stores[store]?._isAvailable ? this._stores[store]!._removeData(key) : null)),
        )

        return this._parseSetterResponse(txs, stores, null)
    }

    protected _parseSetterResponse<T>(responses: (Result<T> | null)[], stores: StorageTypeList, value: T): Result<T> {
        if (responses.every((res) => res === null || res.isErr())) {
            const failed = responses.find((res) => res?.getErr())
            if (failed) {
                StaticEventDispatcher._dispatch(StaticEventDispatcher.EventType.ERROR, failed.getErr())
                this._logger.warn(failed.getErr()!.message)
                return failed
            } else {
                return Result.Err(StorageException.notAvailable(stores))
            }
        }

        responses.forEach((res) => {
            if (res?.isErr()) {
                StaticEventDispatcher._dispatch(StaticEventDispatcher.EventType.ERROR, res.getErr())
            }
        })

        return Result.Ok(value)
    }
}
